import React, { useEffect, useState } from "react";
import { Camera } from "lucide-react";
import { connect } from "react-redux";
import { openMainModal } from "store/actions/profileActions";
import styles from "./FileUploadOnboarding.module.scss";

interface FileUploadOnboardingProps {
  file: File | null;
  setImage: (file: File) => void;
  openMainModal: (config: any) => void;
}

function dataURLtoFile(dataurl: string, filename: string): File | null {
  try {
    if (!dataurl || typeof dataurl !== 'string') {
      console.error("dataURL inválido:", dataurl);
      return null;
    }

    const arr = dataurl.split(',');
    if (arr.length < 2) {
      console.error("Formato dataURL inválido");
      return null;
    }

    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  } catch (error) {
    console.error("Error al convertir dataURL a File:", error);
    return null;
  }
}

function compressImage(file: File, maxWidth: number = 800, quality: number = 0.7): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    let objectURL: string;
    
    img.onload = () => {
      try {
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        ctx?.drawImage(img, 0, 0, width, height);
        
        const compressedDataURL = canvas.toDataURL('image/jpeg', quality);
        
        canvas.width = 0;
        canvas.height = 0;
        
        if (objectURL) {
          URL.revokeObjectURL(objectURL);
        }
        
        resolve(compressedDataURL);
      } catch (error) {
       
        if (objectURL) {
          URL.revokeObjectURL(objectURL);
        }
        reject(error);
      }
    };
    
    img.onerror = () => {
      if (objectURL) {
        URL.revokeObjectURL(objectURL);
      }
      reject(new Error('Error al cargar la imagen'));
    };
    
    objectURL = URL.createObjectURL(file);
    img.src = objectURL;
  });
}

const FileUploadOnboarding: React.FC<FileUploadOnboardingProps> = ({ file, setImage, openMainModal }) => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const storedImage = localStorage.getItem("userImageNew");
    if (storedImage && storedImage.startsWith('data:image/')) {
      try {
        const fileFromBase64 = dataURLtoFile(storedImage, "userImage.jpg");
        if (fileFromBase64) {
          setImage(fileFromBase64);
          setImagePreview(storedImage);
        } else {
          console.error("No se pudo convertir la imagen del localStorage a File.");
          localStorage.removeItem("userImageNew");
        }
      } catch (error) {
        console.error("Error al cargar imagen del localStorage:", error);
        localStorage.removeItem("userImageNew");
      }
    }
  }, []);

  useEffect(() => {
    if (file && file instanceof File && file.size > 0) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (result) {
          setImagePreview(result);
        }
      };
      reader.onerror = () => {
        console.error("Error al leer el archivo");
        setImagePreview(null);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  }, [file]);

  useEffect(() => {
    return () => {
      setImagePreview(null);
      setIsHovered(false);
    };
  }, []);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile && selectedFile instanceof File && selectedFile.size > 0) {
      if (selectedFile.type !== "image/jpeg" && selectedFile.type !== "image/png") {
        openMainModal({
          title: "Formato de imagen no válido",
          body: "Solo se permiten archivos JPEG o PNG",
          iconType: "error",
          isSuccess: true,
          confirmHandler: null,
        });
        return;
      }

      if (selectedFile.size > 10000000) {
        openMainModal({
          title: "Archivo muy grande",
          body: "El archivo no puede superar 10MB. Se comprimirá automáticamente.",
          iconType: "warning",
          isSuccess: true,
          confirmHandler: null,
        });
      }

      setImage(selectedFile);
      
      compressImage(selectedFile, 800, 0.7)
        .then((compressedBase64) => {
          try {
            const estimatedSize = Math.ceil(compressedBase64.length * 1.37);
            const maxLocalStorageSize = 5 * 1024 * 1024; 
            
            if (estimatedSize > maxLocalStorageSize) {
              return compressImage(selectedFile, 600, 0.5);
            }
            
            return compressedBase64;
          } catch (error) {
            throw error;
          }
        })
        .then((finalCompressedBase64) => {
          try {
            localStorage.setItem("userImageNew", finalCompressedBase64);
            
            setImagePreview(finalCompressedBase64);
          } catch (error) {
            if (error instanceof Error && error.name === 'QuotaExceededError') {
              openMainModal({
                title: "Imagen demasiado grande",
                body: "La imagen es demasiado grande incluso después de comprimir. Intenta con una imagen más pequeña o de menor resolución.",
                iconType: "error",
                isSuccess: true,
                confirmHandler: null,
              });
            } else {
              console.error("Error al guardar en localStorage:", error);
              openMainModal({
                title: "Error al guardar imagen",
                body: "Error al guardar la imagen. Intenta nuevamente.",
                iconType: "error",
                isSuccess: true,
                confirmHandler: null,
              });
            }
          }
        })
        .catch((error) => {
          console.error("Error al comprimir imagen:", error);
          openMainModal({
            title: "Error al procesar imagen",
            body: "Error al procesar la imagen. Intenta con otra imagen.",
            iconType: "error",
            isSuccess: true,
            confirmHandler: null,
          });
        });
    }
  };

  return (
    <div className={styles["file-upload-onboarding"]}>
      <div 
        className={styles["image-container"]}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {imagePreview ? (
          <img 
            src={imagePreview} 
            alt="Imagen de perfil" 
            className={styles["profile-image"]}
          />
        ) : (
          <div className={styles["default-image"]}>
            <Camera size={40} />
          </div>
        )}
        
        <input 
          type="file" 
          accept="image/*"
          onChange={handleImageChange}
          className={styles["file-input"]}
          id="onboarding-file-input"
        />
        
        {isHovered && (
          <div className={styles["camera-overlay"]}>
            <Camera size={24} />
            <span>Cambiar imagen</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default connect(null, { openMainModal })(FileUploadOnboarding);
